/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { notNil } from '@gh/core-util';
import {
  Autowired,
  Column,
  Component,
  Events,
  EventService,
  GridOptionsWrapper,
  IComponent,
  IHeader,
  IHeaderParams,
  LongTapEvent,
  RefSelector,
  SortController,
  TouchListener,
  Utils,
} from 'ag-grid-community';

import { isNil } from 'lodash';

export interface HeaderWithExternalFilterCallbackParams {
  setFiltered(filtered: boolean): void;
}

export interface HeaderWithExternalFilterParams extends IHeaderParams {
  title?: string;

  onInit?(params: HeaderWithExternalFilterCallbackParams): void;

  onDestroy?(): void;

  onFilter?(params: HeaderWithExternalFilterCallbackParams): void;
}

export class HeaderWithExternalFilterComponent extends Component implements IComponent<HeaderWithExternalFilterParams>, IHeader {

  private static TEMPLATE =
    '<div class="ag-cell-label-container" role="presentation">' +
    '  <span ref="eExternalFilter" class="ag-header-icon grid-header-cell-external-filter" aria-hidden="true"></span>' +
    '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
    '    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
    '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" aria-hidden="true"></span>' +
    '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" aria-hidden="true"></span>' +
    '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" aria-hidden="true"></span>' +
    '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" aria-hidden="true"></span>' +
    '  </div>' +
    '</div>';

  @Autowired('gridOptionsWrapper') private gridOptionsWrapper: GridOptionsWrapper;
  @Autowired('sortController') private sortController: SortController;
  @Autowired('eventService') private eventService: EventService;

  @RefSelector('eSortAsc') private eSortAsc: HTMLElement;

  @RefSelector('eSortDesc') private eSortDesc: HTMLElement;
  @RefSelector('eSortNone') private eSortNone: HTMLElement;
  @RefSelector('eSortOrder') private eSortOrder: HTMLElement;
  @RefSelector('eExternalFilter') private eExternalFilter: HTMLElement;
  @RefSelector('eLabel') private eLabel: HTMLElement;
  @RefSelector('eText') private eText: HTMLElement;

  private params: HeaderWithExternalFilterParams;

  init(params: HeaderWithExternalFilterParams): void {

    this.setTemplate(HeaderWithExternalFilterComponent.TEMPLATE);
    this.params = params;

    this.setupTap();
    this.setupIcons(params.column);
    this.setupExternalFilter();
    this.setupSort();
    this.setupText(params.displayName);

    if (this.params.onInit) {
      this.params.onInit(this.createCallbackParams());
    }
  }

  destroy(): void {
    if (this.params.onDestroy) {
      this.params.onDestroy();
    }

    super.destroy();
  }

  showExternalFilter(eventSource: HTMLElement): void {
    if (this.params.onFilter) {
      this.params.onFilter(this.createCallbackParams());
    }
  }

  setupSort(): void {
    const enableSorting = this.params.enableSorting;

    if (!enableSorting) {
      this.removeSortIcons();
      return;
    }

    const sortUsingCtrl = this.gridOptionsWrapper.isMultiSortKeyCtrl();

    // add the event on the header, so when clicked, we do sorting
    if (this.eLabel) {
      this.addDestroyableEventListener(this.eLabel, 'click', (event: MouseEvent) => {
        const multiSort = sortUsingCtrl ? (event.ctrlKey || event.metaKey) : event.shiftKey;
        this.params.progressSort(multiSort);
      });
    }

    this.addDestroyableEventListener(this.params.column, Column.EVENT_SORT_CHANGED, this.onSortChanged.bind(this));
    this.onSortChanged();

    this.addDestroyableEventListener(this.eventService, Events.EVENT_SORT_CHANGED, this.setMultiSortOrder.bind(this));
    this.setMultiSortOrder();
  }

  private setupText(displayName: string): void {
    if (this.eText) {
      this.eText.innerHTML = displayName;
    }
  }

  private setupIcons(column: Column): void {
    this.addInIcon('sortAscending', this.eSortAsc, column);
    this.addInIcon('sortDescending', this.eSortDesc, column);
    this.addInIcon('sortUnSort', this.eSortNone, column);
    this.addInIcon('externalFilter', this.eExternalFilter, column);
  }

  private addInIcon(iconName: string, eParent: HTMLElement, column: Column): void {
    if (isNil(eParent)) {
      return;
    }

    const eIcon = Utils.createIconNoSpan(iconName, this.gridOptionsWrapper, column);
    eParent.appendChild(eIcon);
  }

  private setupTap(): void {
    if (this.gridOptionsWrapper.isSuppressTouch()) {
      return;
    }

    const touchListener = new TouchListener(this.getGui());

    if (this.params.enableMenu) {
      const longTapListener = (event: LongTapEvent) => {
        notNil(this.gridOptionsWrapper.getApi()).showColumnMenuAfterMouseClick(this.params.column, event.touchStart);
      };
      this.addDestroyableEventListener(touchListener, TouchListener.EVENT_LONG_TAP, longTapListener);
    }

    if (this.params.enableSorting) {
      const tapListener = () => {
        this.sortController.progressSort(this.params.column, false, 'uiColumnSorted');
      };

      this.addDestroyableEventListener(touchListener, TouchListener.EVENT_TAP, tapListener);
    }

    this.addDestroyFunc(() => touchListener.destroy());
  }

  private setupExternalFilter(): void {

    // if no external filter provided in template, do nothing
    if (!this.eExternalFilter) {
      return;
    }

    this.eExternalFilter.addEventListener('click', () => this.showExternalFilter(this.eExternalFilter));
    if (this.params.title) {
      this.eExternalFilter.title = this.params.title;
    }
  }

  private removeSortIcons(): void {
    Utils.removeFromParent(this.eSortAsc);
    Utils.removeFromParent(this.eSortDesc);
    Utils.removeFromParent(this.eSortNone);
    Utils.removeFromParent(this.eSortOrder);
  }

  private onSortChanged(): void {

    Utils.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-sorted-asc', this.params.column.isSortAscending());
    Utils.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-sorted-desc', this.params.column.isSortDescending());
    Utils.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-sorted-none', this.params.column.isSortNone());

    if (this.eSortAsc) {
      Utils.addOrRemoveCssClass(this.eSortAsc, 'ag-hidden', !this.params.column.isSortAscending());
    }

    if (this.eSortDesc) {
      Utils.addOrRemoveCssClass(this.eSortDesc, 'ag-hidden', !this.params.column.isSortDescending());
    }

    if (this.eSortNone) {
      const alwaysHideNoSort = !this.params.column.getColDef().unSortIcon && !this.gridOptionsWrapper.isUnSortIcon();
      Utils.addOrRemoveCssClass(this.eSortNone, 'ag-hidden', alwaysHideNoSort || !this.params.column.isSortNone());
    }
  }

  // we listen here for global sort events, NOT column sort events, as we want to do this
  // when sorting has been set on all column (if we listened just for our col (where we
  // set the asc / desc icons) then it's possible other cols are yet to get their sorting state.
  private setMultiSortOrder(): void {

    if (!this.eSortOrder) {
      return;
    }

    const col = this.params.column;
    const allColumnsWithSorting = this.sortController.getColumnsWithSortingOrdered();
    const indexThisCol = allColumnsWithSorting.indexOf(col);
    const moreThanOneColSorting = allColumnsWithSorting.length > 1;
    const showIndex = col.isSorting() && moreThanOneColSorting;

    Utils.setVisible(this.eSortOrder, showIndex);

    this.eSortOrder.innerHTML = indexThisCol >= 0 ? (indexThisCol + 1).toString() : '';
  }

  private createCallbackParams(): HeaderWithExternalFilterCallbackParams {
    return { setFiltered: this.setFiltered.bind(this) };
  }

  private setFiltered(filtered: boolean): void {
    this.eExternalFilter.classList.toggle('grid-header-cell-external-filter-filtered', filtered);
  }
}
